import { ethers } from 'ethers';
import Logger from '../utils/logger';

// Configuration RPC avec fallbacks robustes
const ALCHEMY_KEY = process.env.REACT_APP_ALCHEMY_KEY;
const FALLBACK_RPCS = [
    `https://eth-mainnet.g.alchemy.com/v2/${ALCHEMY_KEY}`,
    'https://rpc.ankr.com/eth',
    'https://eth.llamarpc.com',
    'https://cloudflare-eth.com'
].filter(Boolean);

// Contract Addresses (vérifié sur Etherscan)
export const PROXY_ADDRESS = "0xC3b2990027217b9970b2d526aa11Ba3f223eb39C";
export const IMPLEMENTATION_V2_ADDRESS = "0xdb2e16605c672bd0d743142e10ce2c1b12a876a4";
export const WETH_ADDRESS = "0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2"; 
export const UNISWAP_ROUTER = "0x7a250d5630B4cF539739dF2C5dAcb4c659F2488D";
export const UNISWAP_PAIR_ADDRESS = "0x8a1D8f57261e8832CE1D7C525Df76dbe002B2e25";

// Vesting Wallets (validés par le contrat)
export const AUTHORIZED_VESTING_WALLETS = {
    LP1: "0x0D7083D8dCdF1DBc72D1CcD2653f9fDB1981505E",
    LP2: "0xcaDf2f51CB897cb4E476435772c3Ff3572f924e2", 
    LP3: "0xBFc831C5CcF3FE6bF03e0051C72B1066c7A136d9",
    TEAM: "0xeF616AF55083Cb6BDF355a34224FFE829100D9b2",
    ADMIN: "0x5A46d0F5bbce72D9665689cDAe9993824260b882",
    DEV_MARKETING: "0x4ddbb990c286ee71cd128899949e506f78eb08C0"
};

// Network Configuration optimisée V2
export const NETWORK_CONFIG = {
    CHAIN_ID: 1,
    REQUIRED_NETWORK: "Ethereum Mainnet",
    RPC_URL: FALLBACK_RPCS[0],
    EXPLORER_URL: "https://etherscan.io",
    BLOCK_TIME: 12,
    RETRY_TIMEOUT: 10000,
    MAX_RETRIES: 3,
    CONFIRMATIONS_REQUIRED: 2,
    FALLBACK_RPC_URLS: FALLBACK_RPCS,
    WSS_URL: `wss://eth-mainnet.g.alchemy.com/v2/${ALCHEMY_KEY}`,
    HEALTH_CHECK_INTERVAL: 30000,    // 30s intervalle vérification
    MAX_BATCH_REQUEST_SIZE: 100      // Taille max batch RPC
};

// Limites de Transaction selon contrat V2
export const LIMITS = {
    STANDARD_TX_LIMIT: ethers.utils.parseEther("15000"),
    STAKING_TX_LIMIT: ethers.utils.parseEther("50000"),
    MAX_WALLET: ethers.utils.parseEther("100000"),
    MIN_TIME_BETWEEN_TXS: 20,
    PRICE_IMPACT_HIGH: 5,
    PRICE_IMPACT_EXTREME: 10,
    MAX_POSITIONS_PER_USER: 10,
    MIN_STAKE_AMOUNT: ethers.utils.parseEther("100"),
    MAX_PENDING_TRANSACTIONS: 5,
    MIN_LIQUIDITY: ethers.utils.parseEther("1"),
    MAX_SLIPPAGE: 200, // 2%
    FLASH_LOAN_THRESHOLD: 3, // Max tx par bloc
    MAX_BATCH_VESTING_CLAIMS: 20,
    MIN_VESTING_AMOUNT: ethers.utils.parseEther("1000")
};

// Périodes de Lock V2 complètes
export const LOCK_PERIODS = {
    MARKETING: 2592000,                // 30 jours
    DEV_FUND: 5184000,                 // 60 jours
    TRADING: 7776000,                  // 90 jours
    MIN_STAKE: 7776000,
    MAX_STAKE: 31104000,
    CLAIM_COOLDOWN: 86400,             // 24h entre claims
    UNSTAKE_COOLDOWN: 604800,          // 7 jours entre unstakes
    POSITION_LOCK: 1209600,            // 14 jours lock position
    EMERGENCY_UNLOCK: 172800,          // 48h pour unlock emergency
    REWARD_DISTRIBUTION: 43200,        // 12h entre distributions
    LP_LOCK_MINIMUM: 2592000,          // 30 jours minimum lock LP
    BLACKLIST_REVIEW: 1209600,         // 14 jours review blacklist
    EMERGENCY_RECOVERY: 259200,        // 3 jours pour recovery
    MAX_STAKE_EXTENSION: 31536000,     // 1 an max extension
    VESTING_BATCH_DELAY: 604800        // 7 jours entre batches vesting
};

// Configuration Staking mise à jour V2
export const STAKING_CONFIG = {
    BASE_APY: 2000,                    // 20%
    LP_APY: 8000,                      // 80%
    WEEK1_BONUS: 4000,                 // +40%
    WEEK2_BONUS: 2000,                 // +20%
    MAX_DAILY_REWARD_PERCENT: 200,     // 2%
    THRESHOLD_1: '750000',             // 100% APY
    THRESHOLD_2: '500000',             // 75% APY
    THRESHOLD_3: '250000',             // 50% APY
    BURN_RATE: {
        STANDARD: 50,                  // 0.5%
        STAKING: 25                    // 0.25%
    },
    LOCK_PERIODS: {
        THREE_MONTHS: { 
            days: 90, 
            bonus: 500,                // +5%
            timestamp: 7776000         
        },
        SIX_MONTHS: { 
            days: 180, 
            bonus: 1000,               // +10%
            timestamp: 15552000        
        },
        NINE_MONTHS: { 
            days: 270, 
            bonus: 1500,               // +15%
            timestamp: 23328000        
        },
        TWELVE_MONTHS: { 
            days: 360, 
            bonus: 2000,               // +20%
            timestamp: 31104000        
        }
    },
    POSITION_LIMITS: {
        MIN_AMOUNT: ethers.utils.parseEther("100"),
        MAX_POSITIONS: 10,
        MAX_TOTAL_STAKE: ethers.utils.parseEther("100000"),
        CLAIM_COOLDOWN: 86400,
        UNSTAKE_COOLDOWN: 604800
    },
    POSITION_VALIDATION: {
        MAX_EXTEND_DURATION: 31536000,
        MIN_COMPOUND_AMOUNT: ethers.utils.parseEther("100")
    }
};

// Configuration sécurité V2
export const SECURITY_CONFIG = {
    IMPLEMENTATION_SLOT: '0x360894a13ba1a3210667c828492db98dca3e2076cc3735a920a3ca505d382bbc',
    MAX_GAS_LIMIT: 300000,
    MAX_BATCH_SIZE: 10,
    MIN_LIQUIDITY_ETH: ethers.utils.parseEther('1'),
    PRICE_DEVIATION_MAX: 20, // 20%
    FLASH_LOAN_CHECKS: {
        MAX_TPS: 3,
        BLOCK_WINDOW: 5,
        MIN_DELAY: 20
    },
    POSITION_VALIDATION: {
        MIN_AMOUNT: ethers.utils.parseEther('100'),
        MAX_POSITIONS: 10,
        COOLDOWN: 86400
    },
    LP_REQUIREMENTS: {
        MIN_LIQUIDITY: ethers.utils.parseEther('1'),
        MAX_SLIPPAGE: 200, // 2%
        MIN_LOCK: 2592000  // 30 jours
    },
    BLACKLIST_PARAMS: {
        REVIEW_PERIOD: 1209600,  // 14 jours
        MAX_VIOLATIONS: 3,
        AUTO_BAN_THRESHOLD: 5
    }
};

// Constantes Supply selon contrat V2
export const TOKEN_CONSTANTS = {
    INITIAL_SUPPLY: ethers.utils.parseEther("5000000"),
    INITIAL_REWARDS_POOL: ethers.utils.parseEther("1000000"),
    INITIAL_LIQUIDITY_TOKENS: ethers.utils.parseEther("150000"),
    MARKETING_AMOUNT: ethers.utils.parseEther("200000"),
    DEV_FUND_AMOUNT: ethers.utils.parseEther("400000"),
    TRADING_RESERVE_AMOUNT: ethers.utils.parseEther("2410000"),
    MAX_BURN_SUPPLY: ethers.utils.parseEther("2000000"),
    TEAM_INITIAL_CLAIM: ethers.utils.parseEther("80000"),
    // Allocations initiales par adresse
    INITIAL_ALLOCATIONS: {
        TEAM: ethers.utils.parseEther("80000"),  // 80K tokens
        LP1: ethers.utils.parseEther("20000"),   // 20K tokens
        LP2: ethers.utils.parseEther("20000"),   // 20K tokens
        LP3: ethers.utils.parseEther("40000")    // 40K tokens
    }
};

// Messages d'erreur étendus V2
export const ERROR_MESSAGES = {
    UNSUPPORTED_NETWORK: "Please connect to Ethereum Mainnet",
    WALLET_DISCONNECTED: "Please connect your wallet",
    TRADING_DISABLED: "Trading is not enabled yet",
    MAX_BURN_REACHED: "Maximum burn supply reached",
    INSUFFICIENT_BALANCE: "Insufficient balance for transaction",
    EXCEEDS_TX_LIMIT: "Amount exceeds transaction limit",
    EXCEEDS_WALLET_LIMIT: "Amount would exceed maximum wallet limit",
    INSUFFICIENT_POOL: "Insufficient pool balance",
    TOO_FREQUENT: "Please wait between transactions",
    STILL_LOCKED: "Tokens are still locked",
    INITIALIZATION_FAILED: "Contract initialization failed",
    INVALID_AMOUNT: "Invalid amount input",
    PRICE_IMPACT_HIGH: "Warning: High price impact",
    RPC_ERROR: "Network connection error. Please try again.",
    VESTING_UNAUTHORIZED: "Unauthorized wallet for vesting",
    ADDRESS_BLACKLISTED: "Address is blacklisted",
    INVALID_POSITION: "Invalid staking position",
    MAX_POSITIONS_REACHED: "Maximum number of positions reached",
    POSITION_LOCKED: "Position is still locked",
    FLASH_LOAN_DETECTED: "Flash loan protection triggered",
    CLAIM_COOLDOWN: "Please wait 24h between claims",
    UNSTAKE_COOLDOWN: "Please wait 7 days between unstakes",
    INSUFFICIENT_POSITION_BALANCE: "Insufficient balance in position",
    EMERGENCY_PAUSED: "Contract is emergency paused",
    SLIPPAGE_EXCEEDED: "Slippage tolerance exceeded",
    INVALID_IMPLEMENTATION: "Invalid contract implementation version",
    SECURITY_CHECK_FAILED: "Security validation failed",
    LP_MINIMUM_NOT_MET: "Minimum LP lock period not met",
    REWARD_DISTRIBUTION_LOCKED: "Reward distribution still locked",
    BLACKLIST_UNDER_REVIEW: "Address under blacklist review",
    VESTING_BATCH_LOCKED: "Vesting batch release still locked",
    POSITION_EXTEND_LIMIT: "Position extension limit reached",
    COMPOUND_AMOUNT_LOW: "Compound amount below minimum",
    RECOVERY_IN_PROGRESS: "Emergency recovery in progress"
};

// Helper Functions V2 - Corrigé pour la gestion des BigNumber
export const utils = {
    /**
     * Formate un montant en chaîne de caractères avec un nombre spécifié de décimales
     * Version corrigée pour gérer correctement les BigNumber
     * @param {any} amount - Montant à formater (BigNumber, string, number)
     * @param {number} decimals - Nombre de décimales à afficher
     * @returns {string} - Montant formaté
     */
    formatAmount: (amount, decimals = 2) => {
        // Si pas de valeur, renvoyer 0
        if (amount === undefined || amount === null) return '0';
        
        try {
            let valueToFormat;
            
            // 1. Détection et traitement des BigNumber d'ethers.js
            if (amount && typeof amount === 'object' && amount._isBigNumber) {
                // Utiliser formatEther pour convertir le BigNumber en string avec 18 décimales
                valueToFormat = parseFloat(ethers.utils.formatEther(amount));
            }
            // 2. Traitement des chaînes de caractères
            else if (typeof amount === 'string') {
                // Si la chaîne est vide, renvoyer 0
                if (amount.trim() === '') return '0';
                // Tenter de convertir la chaîne en nombre
                valueToFormat = parseFloat(amount);
            }
            // 3. Traitement des nombres
            else if (typeof amount === 'number') {
                valueToFormat = amount;
            }
            // 4. Autre cas (objet, etc.)
            else {
                try {
                    // Tenter de convertir en chaîne puis en nombre
                    valueToFormat = parseFloat(amount.toString());
                } catch (e) {
                    // En cas d'échec, renvoyer 0
                    return '0';
                }
            }
            
            // Vérifier si la valeur finale est valide
            if (isNaN(valueToFormat) || Math.abs(valueToFormat) < 0.000001) {
                return '0';
            }
            
            // Formatter avec le nombre spécifié de décimales
            return valueToFormat.toLocaleString(undefined, {
                minimumFractionDigits: decimals,
                maximumFractionDigits: decimals
            });
        } catch (error) {
            // En cas d'erreur, logger et renvoyer 0
            console.error('Error formatting amount:', error, {
                amountType: typeof amount,
                isBigNumber: amount && typeof amount === 'object' && !!amount._isBigNumber,
                value: amount ? amount.toString() : 'null/undefined'
            });
            return '0';
        }
    },

    /**
     * Valide un montant
     * @param {string|number} amount - Montant à valider
     * @returns {boolean} - Montant valide ou non
     */
    validateAmount: (amount) => {
        try {
            // Si pas de valeur ou valeur négative, invalide
            if (!amount || parseFloat(amount) <= 0) return false;
            
            // Tenter de convertir en BigNumber
            const amountWei = ethers.utils.parseEther(amount.toString());
            return amountWei.gt(0) && amountWei.lt(ethers.constants.MaxUint256);
        } catch {
            return false;
        }
    },

    /**
     * Convertit une valeur en BigNumber
     * @param {any} value - Valeur à convertir
     * @returns {BigNumber} - BigNumber résultant
     */
    toBigNumber: (value) => {
        try {
            if (!value) return ethers.BigNumber.from(0);
            if (value._isBigNumber) return value;
            return ethers.utils.parseEther(value.toString());
        } catch (error) {
            console.error("Error converting to BigNumber:", error, {value});
            return ethers.BigNumber.from(0);
        }
    },

    /**
     * Calcule le bonus d'APY en fonction de la durée de lock
     * @param {number} lockDuration - Durée de lock en secondes
     * @returns {number} - Bonus d'APY
     */
    calculateBonus: (lockDuration) => {
        for (const [period, data] of Object.entries(STAKING_CONFIG.LOCK_PERIODS)) {
            if (lockDuration === data.timestamp) {
                return data.bonus;
            }
        }
        return 0;
    },

    /**
     * Vérifie si un wallet est autorisé pour le vesting
     * @param {string} address - Adresse à vérifier
     * @returns {boolean} - Wallet autorisé ou non
     */
    isValidVestingWallet: (address) => {
        if (!address) return false;
        return Object.values(AUTHORIZED_VESTING_WALLETS)
            .map(addr => addr.toLowerCase())
            .includes(address.toLowerCase());
    },

    /**
     * Calcule l'impact de prix pour un swap
     * @param {BigNumber} amountIn - Montant en entrée
     * @param {BigNumber} reserveIn - Réserve en entrée
     * @param {BigNumber} reserveOut - Réserve en sortie
     * @returns {number} - Impact de prix en pourcentage
     */
    calculatePriceImpact: (amountIn, reserveIn, reserveOut) => {
        try {
            // Vérifier que toutes les valeurs sont des BigNumber
            if (!amountIn || !amountIn._isBigNumber || 
                !reserveIn || !reserveIn._isBigNumber || 
                !reserveOut || !reserveOut._isBigNumber) {
                return 0;
            }
            
            const amountInWithFee = amountIn.mul(997);
            const numerator = amountInWithFee.mul(reserveOut);
            const denominator = reserveIn.mul(1000).add(amountInWithFee);
            const amountOut = numerator.div(denominator);
            
            const currentRate = reserveOut.mul(ethers.constants.WeiPerEther).div(reserveIn);
            const newRate = amountOut.mul(ethers.constants.WeiPerEther).div(amountIn);
            
            const impact = currentRate.sub(newRate).mul(10000).div(currentRate);
            return Number(impact.toString()) / 100;
        } catch (error) {
            Logger.error(error, { context: 'calculatePriceImpact' });
            return 0;
        }
    },

    /**
     * Valide une position de staking
     * @param {Object} position - Position à valider
     * @returns {Object|boolean} - Résultat de validation
     */
    validatePosition: (position) => {
        if (!position || !position.exists) return false;

        // S'assurer que les valeurs sont des nombres
        const now = Math.floor(Date.now() / 1000);
        const lockEndTime = position.lockEndTime ? 
            (typeof position.lockEndTime === 'number' ? position.lockEndTime : parseInt(position.lockEndTime.toString())) : 0;
        const lastClaimTime = position.lastClaimTime ? 
            (typeof position.lastClaimTime === 'number' ? position.lastClaimTime : parseInt(position.lastClaimTime.toString())) : 0;
        
        return {
            isValid: true,
            isLocked: lockEndTime > now,
            canClaim: position.pendingRewards && (
                typeof position.pendingRewards === 'string' ? 
                parseFloat(position.pendingRewards) > 0 : 
                position.pendingRewards.gt && position.pendingRewards.gt(0)
            ) && 
            (!lastClaimTime || now - lastClaimTime >= LOCK_PERIODS.CLAIM_COOLDOWN),
            timeUntilUnlock: Math.max(0, lockEndTime - now)
        };
    },

    /**
     * Calcule les récompenses pour une position
     * @param {Object} position - Position
     * @param {number} currentApy - APY actuel
     * @returns {string} - Récompenses calculées
     */
    calculateRewards: (position, currentApy) => {
        if (!position || !position.amount) return '0';
        
        try {
            // Convertir amount en BigNumber si ce n'est pas déjà le cas
            const amount = typeof position.amount === 'string' ? 
                ethers.utils.parseEther(position.amount) : 
                ethers.BigNumber.from(position.amount);
                
            const now = Math.floor(Date.now() / 1000);
            const lastRewardCalculation = position.lastRewardCalculation ? 
                (typeof position.lastRewardCalculation === 'number' ? 
                    position.lastRewardCalculation : 
                    parseInt(position.lastRewardCalculation.toString())) : 0;
                
            const timeElapsed = now - lastRewardCalculation;
            
            const yearlyReward = amount.mul(Math.floor(currentApy * 100)).div(10000);
            const periodReward = yearlyReward.mul(timeElapsed).div(365 * 24 * 3600);
            
            // Application du plafond hebdomadaire
            const maxWeeklyReward = amount.mul(STAKING_CONFIG.MAX_DAILY_REWARD_PERCENT * 7).div(10000);
            const cappedReward = periodReward.gt(maxWeeklyReward) ? maxWeeklyReward : periodReward;
            
            return ethers.utils.formatEther(cappedReward);
        } catch (error) {
            Logger.error(error, { context: 'calculateRewards' });
            return '0';
        }
    },

    /**
     * Calcule le montant de tokens libérables d'un vesting
     * @param {Object} schedule - Schedule de vesting
     * @returns {string} - Montant libérable
     */
    calculateVestingRelease: (schedule) => {
        if (!schedule || !schedule.isActive) return '0';
        
        try {
            const now = Math.floor(Date.now() / 1000);
            // S'assurer que lockEndTime est un nombre
            const lockEndTime = schedule.lockEndTime ? 
                (typeof schedule.lockEndTime === 'number' ? 
                    schedule.lockEndTime : 
                    parseInt(schedule.lockEndTime.toString())) : 0;
                
            if (now < lockEndTime) return '0';
            
            // S'assurer que startTime est un nombre
            const startTime = schedule.startTime ? 
                (typeof schedule.startTime === 'number' ? 
                    schedule.startTime : 
                    parseInt(schedule.startTime.toString())) : 0;
                
            const weeksElapsed = Math.floor((now - startTime) / (7 * 24 * 3600));
            
            // Convertir weeklyAmount en BigNumber si ce n'est pas déjà le cas
            const weeklyAmount = typeof schedule.weeklyAmount === 'string' ? 
                ethers.utils.parseEther(schedule.weeklyAmount) : 
                schedule.weeklyAmount;
                
            let totalVested = weeklyAmount.mul(weeksElapsed);
            
            // Convertir totalAmount en BigNumber si ce n'est pas déjà le cas
            const totalAmount = typeof schedule.totalAmount === 'string' ? 
                ethers.utils.parseEther(schedule.totalAmount) : 
                schedule.totalAmount;
                
            if (totalVested.gt(totalAmount)) {
                totalVested = totalAmount;
            }
            
            // Convertir claimed en BigNumber si ce n'est pas déjà le cas
            const claimed = typeof schedule.claimed === 'string' ? 
                ethers.utils.parseEther(schedule.claimed) : 
                schedule.claimed;
                
            return ethers.utils.formatEther(totalVested.sub(claimed));
        } catch (error) {
            Logger.error(error, { context: 'calculateVestingRelease' });
            return '0';
        }
    },

    /**
     * Formate une durée en jours, heures, minutes
     * @param {number} duration - Durée en secondes
     * @returns {string} - Durée formatée
     */
    formatLockDuration: (duration) => {
        if (!duration) return "0d 0h";
        
        const days = Math.floor(duration / (24 * 3600));
        const hours = Math.floor((duration % (24 * 3600)) / 3600);
        const minutes = Math.floor((duration % 3600) / 60);
        
        if (days > 0) return `${days}d ${hours}h`;
        if (hours > 0) return `${hours}h ${minutes}m`;
        return `${minutes}m`;
    },

    /**
     * Vérifie l'implémentation du contrat
     * @param {Object} provider - Provider ethers.js
     * @returns {Promise<boolean>} - Implémentation valide ou non
     */
    validateImplementation: async (provider) => {
        try {
            if (!provider) return false;
            
            const implementation = await provider.getStorageAt(
                PROXY_ADDRESS,
                SECURITY_CONFIG.IMPLEMENTATION_SLOT
            );
            
            if (!implementation) return false;
            
            const currentImpl = '0x' + implementation.slice(26).toLowerCase();
            return currentImpl === IMPLEMENTATION_V2_ADDRESS.toLowerCase();
        } catch (error) {
            Logger.error(error, { context: 'validateImplementation' });
            return false;
        }
    },

    /**
     * Formate une adresse en forme courte
     * @param {string} address - Adresse à formater
     * @returns {string} - Adresse formatée
     */
    formatAddress: (address) => {
        if (!address) return '';
        if (address.length < 10) return address;
        return `${address.slice(0, 6)}...${address.slice(-4)}`;
    },

    /**
     * Vérifie si une adresse est l'admin
     * @param {string} address - Adresse à vérifier
     * @returns {boolean} - Est admin ou non
     */
    isAdmin: (address) => {
        if (!address) return false;
        return address.toLowerCase() === AUTHORIZED_VESTING_WALLETS.ADMIN.toLowerCase();
    },

    /**
     * Vérifie si une adresse est un wallet de l'équipe
     * @param {string} address - Adresse à vérifier
     * @returns {boolean} - Est team ou non
     */
    isTeam: (address) => {
        if (!address) return false;
        return address.toLowerCase() === AUTHORIZED_VESTING_WALLETS.TEAM.toLowerCase();
    },

    /**
     * Obtient le type de wallet en fonction de l'adresse
     * @param {string} address - Adresse
     * @returns {string|null} - Type de wallet
     */
    getWalletType: (address) => {
        if (!address) return null;
        const normalizedAddress = address.toLowerCase();
        
        for (const [type, walletAddress] of Object.entries(AUTHORIZED_VESTING_WALLETS)) {
            if (walletAddress.toLowerCase() === normalizedAddress) {
                return type;
            }
        }
        
        return null;
    }
};

// Export de la fonction formatAmount pour accès direct
export const formatAmount = utils.formatAmount;

// Export des événements de contrat
export const CONTRACT_EVENTS = {
    Staked: 'Staked(address,uint256,uint256)',
    Unstaked: 'Unstaked(address,uint256)',
    RewardsClaimed: 'RewardsClaimed(address,uint256)',
    LPStaked: 'LPStaked(address,uint256)',
    LPUnstaked: 'LPUnstaked(address,uint256)',
    VestingClaimed: 'VestingClaimed(address,uint256)',
    BlacklistUpdated: 'BlacklistUpdated(address,bool)',
    EmergencyStateUpdated: 'EmergencyStateUpdated(bool,string)',
    PositionCreated: 'StakePositionCreated(address,uint256,uint256)',
    PositionUpdated: 'StakePositionUpdated(address,uint256,uint256)',
    ImplementationUpgraded: 'Upgraded(address)'
};

// Export Contract ABI
export const CONTRACT_ABI = [
  // ERC20
  {
    inputs: [],
    stateMutability: "nonpayable",
    type: "constructor"
  },
  {
    inputs: [],
    name: "initialize",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [],
    name: "name",
    outputs: [{ type: "string", name: "" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [],
    name: "symbol", 
    outputs: [{ type: "string", name: "" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [],
    name: "decimals",
    outputs: [{ type: "uint8", name: "" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [],
    name: "totalSupply",
    outputs: [{ type: "uint256", name: "" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [{ type: "address", name: "account" }],
    name: "balanceOf",
    outputs: [{ type: "uint256", name: "" }],
    stateMutability: "view", 
    type: "function"
  },
  {
    inputs: [
      { type: "address", name: "recipient" },
      { type: "uint256", name: "amount" }
    ],
    name: "transfer",
    outputs: [{ type: "bool", name: "" }],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      { type: "address", name: "owner" },
      { type: "address", name: "spender" }
    ],
    name: "allowance",
    outputs: [{ type: "uint256", name: "" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [
      { type: "address", name: "spender" },
      { type: "uint256", name: "amount" }
    ],
    name: "approve",
    outputs: [{ type: "bool", name: "" }],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      { type: "address", name: "sender" },
      { type: "address", name: "recipient" },
      { type: "uint256", name: "amount" }
    ],
    name: "transferFrom",
    outputs: [{ type: "bool", name: "" }],
    stateMutability: "nonpayable",
    type: "function"
  },

  // Constants & State Variables
  {
    inputs: [],
    name: "INITIAL_SUPPLY",
    outputs: [{ type: "uint256", name: "" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [],
    name: "STANDARD_TX_LIMIT",
    outputs: [{ type: "uint256", name: "" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [],
    name: "STAKING_TX_LIMIT",
    outputs: [{ type: "uint256", name: "" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [],
    name: "MAX_WALLET",
    outputs: [{ type: "uint256", name: "" }],
    stateMutability: "view",
    type: "function"
  },

  // Trading & Liquidity
  {
    inputs: [],
    name: "adminClaimInitialLiquidity",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [{ type: "address", name: "_uniswapPair" }],
    name: "setupInitialLiquidity",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [],
    name: "lockInitialLiquidity",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [],
    name: "claimInitialAllocation",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [],
    name: "enableTrading",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },

  // Staking Functions
  {
    inputs: [
      { type: "uint256", name: "amount" }
    ],
    name: "stake",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      { type: "uint256", name: "amount" },
      { type: "uint256", name: "lockDuration" }
    ],
    name: "stakeWithLock",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [{ type: "uint256", name: "positionId" }],
    name: "unstakePosition",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [{ type: "uint256", name: "amount" }],
    name: "stakeLPTokens",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [{ type: "uint256", name: "amount" }],
    name: "unstakeLPTokens",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },

  // Vesting Functions
  {
    inputs: [
      { type: "address", name: "beneficiary" },
      { type: "uint256", name: "totalAmount" },
      { type: "uint256", name: "vestingWeeks" },
      { type: "uint256", name: "lockDuration" }
    ],
    name: "initializeVesting",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      { type: "address[]", name: "beneficiaries" },
      { type: "uint256[]", name: "vestingIds" },
      { type: "uint256[]", name: "amounts" }
    ],
    name: "batchReleaseVesting",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [{ type: "uint256", name: "amount" }],
    name: "releaseTradeReserveToPool",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },

  // Admin Functions
  {
    inputs: [
      { type: "address", name: "account" },
      { type: "bool", name: "maxWalletExempt_" },
      { type: "bool", name: "txLimitExempt_" }
    ],
    name: "setWhitelist",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      { type: "address", name: "account" },
      { type: "bool", name: "blacklisted" }
    ],
    name: "updateBlacklist",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [],
    name: "pause",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [],
    name: "unpause",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [],
    name: "emergencyWithdraw",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },

  // View Functions
  {
    inputs: [{ type: "address", name: "user" }],
    name: "getStakePositions",
    outputs: [
      {
        components: [
          { type: "uint96", name: "positionId" },
          { type: "uint96", name: "amount" },
          { type: "uint64", name: "timestamp" },
          { type: "uint64", name: "lockEndTime" },
          { type: "uint64", name: "lastRewardCalculation" },
          { type: "uint64", name: "lastWeeklyReward" },
          { type: "uint32", name: "bonusApy" },
          { type: "uint128", name: "pendingRewards" },
          { type: "bool", name: "isLocked" },
          { type: "bool", name: "exists" },
          { type: "uint8", name: "padding" }
        ],
        type: "tuple[]"
      }
    ],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [
      { type: "address", name: "wallet" },
      { type: "uint256", name: "vestingId" }
    ],
    name: "getVestingInfo",
    outputs: [
      {
        components: [
          { type: "uint256", name: "totalAmount" },
          { type: "uint256", name: "weeklyAmount" },
          { type: "uint256", name: "startTime" },
          { type: "uint256", name: "lockEndTime" },
          { type: "uint256", name: "lastClaimTime" },
          { type: "uint256", name: "endTime" },
          { type: "uint256", name: "claimed" },
          { type: "bool", name: "isLP" },
          { type: "bool", name: "isActive" }
        ],
        type: "tuple"
      },
      { type: "uint256", name: "available" }
    ],
    stateMutability: "view",
    type: "function"
  },

  // Events
  {
    anonymous: false,
    inputs: [
      { indexed: true, type: "address", name: "user" },
      { type: "uint256", name: "amount" },
      { type: "uint256", name: "lockDuration" }
    ],
    name: "Staked",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, type: "address", name: "user" },
      { type: "uint256", name: "amount" }
    ],
    name: "Unstaked",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, type: "address", name: "user" },
      { type: "uint256", name: "amount" }
    ],
    name: "RewardsClaimed",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      { type: "uint256", name: "amount" },
      { type: "uint256", name: "newTotalBurned" },
      { type: "uint256", name: "timestamp" }
    ],
    name: "BurnExecuted",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, type: "address", name: "user" },
      { type: "uint256", name: "amount" }
    ],
    name: "LPStaked",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, type: "address", name: "user" },
      { type: "uint256", name: "amount" }
    ],
    name: "LPUnstaked",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, type: "address", name: "beneficiary" },
      { type: "uint256", name: "amount" }
    ],
    name: "VestingClaimed",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      { type: "uint256", name: "beneficiariesCount" },
      { type: "uint256", name: "totalAmount" }
    ],
    name: "VestingBatchReleased",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, type: "address", name: "pair" },
      { type: "uint256", name: "lpAmount" }
    ],
    name: "InitialLiquiditySetup",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      { type: "uint256", name: "lpAmount" },
      { type: "uint256", name: "timestamp" }
    ],
    name: "InitialLiquidityLocked",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      { type: "uint256", name: "amount" },
      { type: "uint256", name: "timestamp" },
      { type: "uint256", name: "remainingReserve" }
    ],
    name: "TradeReserveReleasedToPool",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, type: "address", name: "user" },
      { type: "uint256", name: "positionId" },
      { type: "uint256", name: "amount" }
    ],
    name: "StakePositionCreated",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, type: "address", name: "user" },
      { type: "uint256", name: "positionId" },
      { type: "uint256", name: "amount" }
    ],
    name: "StakePositionUpdated",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, type: "address", name: "account" },
      { type: "bool", name: "status" }
    ],
    name: "BlacklistUpdated",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      { type: "bool", name: "active" },
      { type: "string", name: "reason" }
    ],
    name: "EmergencyStateUpdated",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, type: "address", name: "beneficiary" },
      { type: "uint256", name: "amount" }
    ],
    name: "InitialAllocationClaimed",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, type: "address", name: "beneficiary" },
      { type: "uint256", name: "newAmount" }
    ],
    name: "WeeklyVestingUpdated",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [{ type: "uint256", name: "timestamp" }],
    name: "Paused",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [{ type: "uint256", name: "timestamp" }],
    name: "Unpaused",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, type: "address", name: "user" },
      { type: "uint256", name: "newAmount" }
    ],
    name: "StakingUpdated",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      { type: "uint256", name: "newAmount" },
      { type: "uint256", name: "timestamp" }
    ],
    name: "RewardPoolUpdated",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, type: "address", name: "user" },
      { type: "uint256", name: "amount" }
    ],
    name: "EmergencyWithdraw",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, type: "address", name: "account" },
      { type: "bool", name: "maxWalletExempt" },
      { type: "bool", name: "txLimitExempt" }
    ],
    name: "WhitelistUpdated",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [{ type: "address", name: "newImplementation" }],
    name: "Upgraded",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, type: "address", name: "previousOwner" },
      { indexed: true, type: "address", name: "newOwner" }
    ],
    name: "OwnershipTransferred",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, type: "address", name: "from" },
      { indexed: true, type: "address", name: "to" },
      { type: "uint256", name: "amount" }
    ],
    name: "Transfer",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, type: "address", name: "owner" },
      { indexed: true, type: "address", name: "spender" },
      { type: "uint256", name: "amount" }
    ],
    name: "Approval",
    type: "event"
  }
];

export default {
    utils,
    NETWORK_CONFIG,
    LIMITS,
    LOCK_PERIODS,
    STAKING_CONFIG,
    TOKEN_CONSTANTS,
    SECURITY_CONFIG,
    ERROR_MESSAGES,
    CONTRACT_EVENTS
};
